
import { useState, useEffect } from 'react';

interface AppPremium {
  id: number;
  name: string;
  description: string;
  link: string;
  icon: string;
}

const ICON_COLORS: Record<string, string> = {
  'ri-telegram-line': 'from-teal-500 to-teal-600',
  'ri-whatsapp-line': 'from-green-500 to-green-600',
  'ri-instagram-line': 'from-pink-500 to-purple-600',
  'ri-youtube-line': 'from-red-500 to-red-600',
  'ri-music-line': 'from-gray-800 to-gray-900',
  'ri-spotify-line': 'from-green-600 to-green-700',
  'ri-netflix-line': 'from-red-600 to-red-700',
  'ri-tv-line': 'from-orange-500 to-orange-600',
};

export default function ApkPremium() {
  const [apps, setApps] = useState<AppPremium[]>([]);
  const [announcement, setAnnouncement] = useState('');

  useEffect(() => {
    const saved = localStorage.getItem('adminApps');
    if (saved) {
      setApps(JSON.parse(saved));
    } else {
      setApps([
        { id: 1, name: 'Telegram Premium', description: 'Version premium avec fonctionnalités avancées', link: 'https://t.me/+RLed8IfVgMINDg0', icon: 'ri-telegram-line' },
        { id: 2, name: 'WhatsApp Plus', description: 'WhatsApp avec options supplémentaires', link: 'https://whatsapp.com/channel/0029VbCEuEA1NCrMAsPILy3J', icon: 'ri-whatsapp-line' },
        { id: 3, name: 'Instagram Pro', description: 'Téléchargez photos et vidéos facilement', link: '', icon: 'ri-instagram-line' },
        { id: 4, name: 'YouTube Premium', description: 'Sans publicités et téléchargements', link: '', icon: 'ri-youtube-line' },
        { id: 5, name: 'TikTok Pro', description: 'Téléchargez vidéos sans filigrane', link: '', icon: 'ri-music-line' },
        { id: 6, name: 'Spotify Premium', description: 'Musique illimitée sans pub', link: '', icon: 'ri-spotify-line' },
        { id: 7, name: 'Netflix Premium', description: 'Accès illimité aux films et séries', link: '', icon: 'ri-netflix-line' },
        { id: 8, name: 'Canal+ Premium', description: 'Toutes les chaînes Canal+ en direct', link: '', icon: 'ri-tv-line' },
      ]);
    }
    const ann = localStorage.getItem('siteAnnouncement') || '';
    setAnnouncement(ann);
  }, []);

  const handleDownload = (app: AppPremium) => {
    if (app.link) {
      window.open(app.link, '_blank');
    } else {
      alert(`Le téléchargement de "${app.name}" sera bientôt disponible. Restez connecté !`);
    }
  };

  return (
    <div className="space-y-6">
      {announcement && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg flex items-start gap-3">
          <i className="ri-megaphone-line text-yellow-500 text-xl mt-0.5"></i>
          <div>
            <p className="text-xs font-bold text-yellow-700 mb-1">Annonce de l'administration</p>
            <p className="text-sm text-gray-700">{announcement}</p>
          </div>
        </div>
      )}

      <div className="bg-gradient-to-r from-indigo-600 to-teal-600 rounded-xl p-8 text-white shadow-lg">
        <h2 className="text-2xl font-bold mb-2">APK Premium</h2>
        <p className="text-indigo-100">Accédez aux versions premium de vos applications préférées</p>
      </div>

      {apps.length === 0 ? (
        <div className="bg-white rounded-xl shadow-lg p-12 text-center">
          <i className="ri-smartphone-line text-5xl text-gray-200 mb-3"></i>
          <p className="text-gray-500 font-semibold">Aucune application disponible pour le moment</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {apps.map((app) => {
            const gradient = ICON_COLORS[app.icon] || 'from-teal-500 to-teal-600';
            return (
              <div key={app.id} className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition">
                <div className={`w-16 h-16 bg-gradient-to-br ${gradient} rounded-2xl flex items-center justify-center mb-4`}>
                  <i className={`${app.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">{app.name}</h3>
                <p className="text-sm text-gray-600 mb-4">{app.description}</p>
                <button
                  onClick={() => handleDownload(app)}
                  className={`w-full py-2.5 rounded-lg font-semibold transition whitespace-nowrap cursor-pointer ${
                    app.link
                      ? 'bg-teal-500 text-white hover:bg-teal-600'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                  }`}
                >
                  {app.link ? 'Accéder' : 'Bientôt disponible'}
                </button>
              </div>
            );
          })}
        </div>
      )}

      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-lg">
        <div className="flex items-start gap-3">
          <i className="ri-alert-line text-2xl text-yellow-600 mt-0.5"></i>
          <div>
            <h4 className="font-bold text-gray-800 mb-1">Avertissement</h4>
            <p className="text-sm text-gray-700">
              Ces applications sont fournies à titre informatif. Assurez-vous de respecter les conditions d'utilisation et les lois en vigueur dans votre pays.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl p-6 shadow-lg">
        <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
          <i className="ri-telegram-line text-teal-600"></i> Besoin d'aide ?
        </h3>
        <p className="text-gray-700 text-sm mb-4">
          Rejoignez notre communauté sur Telegram pour obtenir de l'aide et découvrir plus d'applications premium.
        </p>
        <a
          href="https://t.me/+RLed8IfVgMINDg0"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-lg hover:shadow-lg transition whitespace-nowrap cursor-pointer"
        >
          <i className="ri-telegram-line text-xl"></i>
          <span className="font-semibold">Rejoindre Telegram</span>
        </a>
      </div>
    </div>
  );
}
